import {
  faArrowRight,
  faMinus,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { decompressFromUint8Array } from "lz-string";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AppBar from "../../common/AppBar";
import Loader from "../../common/Loader";
import Snackbar from "../../common/Snackbar";
import { createCanvas } from "../../redux/stepSlice";
import { generateSHA256 } from "../../util/generateSHA256";
import WebcamPopup from "./components/webcam/WebcamPopup";
import { v4 as uuidv4 } from "uuid";
import {
  calculateGroupHeight,
  hasNoteContent,
  hasTaskContent,
  hasUserContent,
  persistCanvasPages,
  convertBlobToBase64,
} from "../SectionElements/plugins/utils";
import { loadFromIndexedDB, openCanvasDB } from "../../util/indexedDBUtils";
import { DB_VERSION } from "../../util/canvas/canvasDbConfig";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import SearchBarWithFilter from "../../components/SearchBarWithFilter";
import axios from "axios";
import {
  createCategory,
  getAllCategories,
  updateCategory,
} from "../../redux/categorySlice";
import {
  createCanvasPageTemplate,
  updateCanvasPageTemplate,
} from "../../redux/canvasTemplatesSlice";
import CategoryPopup from "../Category/CategoryPopup";

import { generateSnapshotFromPageDataHTML } from "../../util/canvas/canvasHtmlSnapshot";
import { sanitizeId } from "./utils/textDataUtils";
import {
  wrapTextToLines,
  breakLongWord,
  wrapFragmentsToLines,
} from "./utils/textWrappingUtils";
import {
  drawStyledLine,
  drawHtmlToCanvas,
  parseHtmlFragments,
} from "./utils/htmlRenderingUtils";
import {
  compressImage,
  uploadToS3,
  convertColorValue,
} from "./utils/imageUploadUtils";
import {
  calculateTextBlockWidth,
  convertTextToPayload,
} from "./utils/textProcessingUtils";
import {
  generateSnapshotFromResponse,
  getMarkersFromIndexedDB,
  generateLayoutConfig,
  saveCanvasElementsToIndexedDB,
} from "./utils/templateUtils";
import {
  addImageToTemplate,
  saveImageToIndexedDB,
} from "./utils/templateImageUtils";
import {
  addTextToTemplate,
  handlePasteButtonClick as handlePasteButtonClickUtil,
} from "./utils/templateTextUtils";
import {
  handleEditCategory as handleEditCategoryUtil,
  handleSaveCategoryAdd as handleSaveCategoryAddUtil,
  handleSaveCategoryEdit as handleSaveCategoryEditUtil,
} from "./utils/templateCategoryUtils";
import { convertApiShapesToMyCanvasFormat } from "./utils/templateConversionUtils";
import {
  pushShapes,
  getRemovedIdsForTemplatePage,
  dedupeById,
  deduplicateByName,
  mergeMainAndOffCanvasElements,
} from "./utils/templateSaveUtils";
import { processImageForSave } from "./utils/templateImageSaveUtils";
import { handleNavigateToCanvas as handleNavigateToCanvasUtil } from "./utils/templateNavigationUtils";
import {
  safeSetCanvasSnapshot,
  getCanvasSnapshotFromStorage,
  clearCanvasSnapshotFromStorage,
} from "./utils/canvasSnapshotStorage";
import {
  ensureCanvasElementsStore,
  hydrateElements,
  getValidLocalStorageValue,
  uniqueById,
} from "./utils/templateLoadingUtils";
import {
  preparePageDataForSnapshot,
  regenerateSnapshotIfNeeded,
  createSnapshotUpdateChecker,
  loadUpdatedDataFromIndexedDB,
} from "./utils/templateDataProcessingUtils";
import {
  handleSort as handleSortUtil,
  showSnackbar as showSnackbarUtil,
  handleCloseSnackbar as handleCloseSnackbarUtil,
  handleCloseModal as handleCloseModalUtil,
  generateSnapshotFromResponseWithState as generateSnapshotFromResponseWithStateUtil,
} from "./utils/templateUIUtils";
import {
  generateSnapshotFromPageData,
  generateSnapshotFromElements,
} from "./utils/snapshotUtils";
import { buildPermissionContext } from "../../util/permissions";
import { BASE_URL } from "../../config";
const TEXT_BOX_MATCH_TOLERANCE = 1;

const toNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getTextDimensions = (text = {}) => {
  const fallback = text.canvas_page_location_and_size || {};
  return {
    x: text.x ?? fallback.x ?? 0,
    y: text.y ?? fallback.y ?? 0,
    width: text.width ?? fallback.width ?? 0,
    height: text.height ?? fallback.height ?? 0,
    rotation: text.rotation ?? fallback.rotation ?? 0,
  };
};

const getTextBoxDimensions = (textBox = {}) => {
  return textBox.canvas_page_location_and_size || {};
};

const valuesAreClose = (a, b, tolerance = TEXT_BOX_MATCH_TOLERANCE) =>
  Math.abs(toNumber(a) - toNumber(b)) <= tolerance;

const textMatchesTextBox = (text, textBox) => {
  if (!text || !textBox) return false;

  const textDims = getTextDimensions(text);
  const textBoxDims = getTextBoxDimensions(textBox);

  return (
    valuesAreClose(textDims.x, textBoxDims.x) &&
    valuesAreClose(textDims.y, textBoxDims.y) &&
    valuesAreClose(textDims.width, textBoxDims.width) &&
    valuesAreClose(textDims.height, textBoxDims.height) &&
    valuesAreClose(textDims.rotation, textBoxDims.rotation || 0)
  );
};

const attachTextBoxMetadataToTexts = (texts = [], textBoxes = []) => {
  if (
    !Array.isArray(texts) ||
    !Array.isArray(textBoxes) ||
    textBoxes.length === 0
  ) {
    return;
  }

  const remaining = [...textBoxes];

  texts.forEach((text) => {
    if (!text) {
      return;
    }

    const existingId =
      text.text_box_id ||
      text.textboxId ||
      text.textBoxId ||
      text.canvas_text_box_id ||
      text.canvasTextBoxId ||
      text.shape_info?.text_box_id ||
      text.shape_properties?.text_box_id;

    let matchIndex = -1;

    if (existingId) {
      matchIndex = remaining.findIndex(
        (tb) => tb?.text_box_id && tb.text_box_id === existingId,
      );
    }

    if (matchIndex === -1) {
      matchIndex = remaining.findIndex((tb) => textMatchesTextBox(text, tb));
    }

    if (matchIndex === -1) {
      return;
    }

    const matched = remaining[matchIndex];
    text.text_box_id = text.text_box_id || matched.text_box_id;
    text.text_box_content_data = (matched.text_box_content || []).map(
      (entry) => ({
        ...entry,
        text_box_id: matched.text_box_id,
      }),
    );
    if (matched.text_box_properties && !text.text_box_properties) {
      text.text_box_properties = matched.text_box_properties;
    }
    if (
      matched.canvas_page_location_and_size &&
      !text.canvas_page_location_and_size
    ) {
      text.canvas_page_location_and_size =
        matched.canvas_page_location_and_size;
    }

    remaining.splice(matchIndex, 1);
  });
};

const flattenTextBoxContent = (textBoxes = []) => {
  if (!Array.isArray(textBoxes) || textBoxes.length === 0) {
    return [];
  }

  const entries = [];
  textBoxes.forEach((textBox) => {
    if (!textBox) {
      return;
    }
    const contentArray = textBox.text_box_content;
    if (!Array.isArray(contentArray) || contentArray.length === 0) {
      return;
    }
    contentArray.forEach((content) => {
      entries.push({
        ...content,
        text_box_id: textBox.text_box_id,
      });
    });
  });

  return entries;
};

// generateSnapshotFromPageData is now imported from utils/snapshotUtils

const AddCanvasTemplate = () => {
  // Always open this screen from the top
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, []);
  const location = useLocation();
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryName, setCategoryName] = useState(
    localStorage.getItem("canvas_category_name") || "",
  );
  const [categoryId, setCategoryId] = useState(
    localStorage.getItem("canvas_category_id") || "",
  );
  const catTableContnrRef = useRef(null);
  const [newCategory, setNewCategory] = useState({
    category_name: "",
    field_name: "",

    description: "",
  });
  const [searchQuery, setSearchQuery] = useState(""); // For filtering parts
  const [searchType, setSearchType] = useState("all_columns");
  const [selectedColumns, setSelectedColumns] = useState({
    field_name: true,
    category_name: true,
    description: true,
  });
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedRowData, setEditedRowData] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [newRow, setNewRow] = useState(null);
  // handleEditCategory is now imported from utils/templateCategoryUtils
  const handleEditCategory = (category) => {
    handleEditCategoryUtil(category, setEditingRowId, setEditedRowData);
  };
  const filterOptions = [
    { value: "all", label: "All Fields" },
    { value: "rich_text_category", label: "Rich Text > Rich Text Category" },
    {
      value: "bom_table_for_step_title",
      label: "Step > BOM Table for Step Title",
    },
    {
      value: "part_number_column_title",
      label: "Step > Item Code Column Title",
    },
  ];
  // handleSaveCategoryAdd and handleSaveCategoryEdit are now imported from utils/templateCategoryUtils
  const handleSaveCategoryAdd = async (row) => {
    return handleSaveCategoryAddUtil(row, {
      setIsUpdating,
      dispatch,
      setEditingRowId,
      setEditedRowData,
      setNewRow,
      setNewCategory,
      showSnackbar,
      createCategory,
      getAllCategories,
    });
  };

  const handleSaveCategoryEdit = async (id) => {
    return handleSaveCategoryEditUtil(id, editedRowData, {
      setIsUpdating,
      dispatch,
      setEditingRowId,
      setEditedRowData,
      showSnackbar,
      updateCategory,
      getAllCategories,
    });
  };
  // Get location state values first
  const snapshot = location.state?.snapshot; // Getting snapshot from location state
  const hasCanvasChanges = location.state?.hasChanges || false;
  const pageId = location.state?.pageId;
  const editMode = location.state?.editMode || false;
  const templateId = location.state?.template?.canvas_page_template_id || null;
  const returnSearchQuery = location.state?.returnSearchQuery || "";

  // State to store the fetched template data
  const [template, setTemplate] = useState({});

  const [canvasName, setCanvasName] = useState(() => {
    const saved = localStorage.getItem("canvas_name");
    return saved && saved !== "undefined" ? saved : "";
  });

  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(editMode);
  const [snapshotGenerating, setSnapshotGenerating] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ step: "", percentage: 0 });
  const [zoomLevel, setZoomLevel] = useState(100); // Track zoom level percentage

  // Check if we have a saved canvas snapshot using the new approach
  const savedSnapshot = getCanvasSnapshotFromStorage();
  const canvasStateInfo = sessionStorage.getItem("canvasStateInfo");

  // Legacy check for backward compatibility
  const legacyState = localStorage.getItem("canvasState");

  // Get legacy snapshot if available
  let legacySnapshot = null;
  if (legacyState) {
    try {
      legacySnapshot = JSON.parse(legacyState).snapshot;
    } catch (e) {}
  }

  // Initialize snapshot state from various sources (like EditCanvasPage)
  const initialSnapshot = snapshot || savedSnapshot || legacySnapshot;

  // Get any existing snapshots
  // Initialize canvasPages with location state data
  const [canvasPages, setCanvasPages] = useState(() => {
    const savedPagesRaw = localStorage.getItem("canvasPages");

    try {
      const savedPages = savedPagesRaw ? JSON.parse(savedPagesRaw) : null;

      if (Array.isArray(savedPages) && savedPages.length > 0) {
        // Pages already exist in localStorage - ensure they have zoomLevel
        const pagesWithZoom = savedPages.map((page) => ({
          ...page,
          zoomLevel: page.zoomLevel || 100,
        }));
        return pagesWithZoom;
      }
    } catch (error) {}

    // No saved pages, create a default one
    const newPage = {
      id: uuidv4(),
      snapshot: initialSnapshot || null,
      zoomLevel: 100,
      elements: {
        texts: [],
        rectangles: [],
        circles: [],
        arrows: [],
        scribbles: [],
        lines: [],
        doubleSidedArrows: [],
        images: [],
      },
      shortDescription: "",
      lastModified: new Date().toISOString(),
      text_boxes: [],
      text_box_content_data: [],
    };

    const defaultPages = [newPage];
    persistCanvasPages(defaultPages);
    return defaultPages;
  });

  // Early IndexedDB hydration - similar to CanvasForm.js for better performance
  // This loads from IndexedDB immediately, especially important in edit mode
  const hasHydratedRef = useRef(false);
  useEffect(() => {
    const hydrateElementsEarly = async () => {
      if (canvasPages.length === 0) return;

      // In edit mode, ALWAYS hydrate from IndexedDB early to get the latest elements and snapshots
      // This is critical because IndexedDB has the latest data including newly created elements
      const shouldForceHydrate = editMode;

      // Only hydrate once initially, or when pages change significantly
      if (hasHydratedRef.current && !shouldForceHydrate) {
        return;
      }

      try {
        const hydratedPages = await Promise.all(
          canvasPages.map(async (page) => {
            try {
              const pageIdToUse = page.id || page.pageId;
              if (!pageIdToUse) {
                return page;
              }

              // Try both original and sanitized pageId (like CanvasForm.js)
              const sanitizedPageId = sanitizeId(pageIdToUse, pageIdToUse);
              let result = null;
              let offCanvasResult = null;

              try {
                result = await loadFromIndexedDB("canvasElements", pageIdToUse);
                offCanvasResult = await loadFromIndexedDB(
                  "offCanvasElements",
                  pageIdToUse,
                );
              } catch (error) {
              }

              if (!result && sanitizedPageId !== pageIdToUse) {
                try {
                  result = await loadFromIndexedDB(
                    "canvasElements",
                    sanitizedPageId,
                  );
                  offCanvasResult = await loadFromIndexedDB(
                    "offCanvasElements",
                    sanitizedPageId,
                  );
                } catch (error) {
                }
              }

              const elements = result?.data || result || {};
              const offCanvasElements =
                offCanvasResult?.data || offCanvasResult || {};
              const mergedElements = mergeMainAndOffCanvasElements(
                elements,
                offCanvasElements,
              );

              // In edit mode, prefer IndexedDB data over page.elements (which might be stale)
              const hasIndexedDBElements =
                mergedElements.texts?.length > 0 ||
                mergedElements.rectangles?.length > 0 ||
                mergedElements.images?.length > 0 ||
                mergedElements.circles?.length > 0 ||
                mergedElements.arrows?.length > 0;

              const useIndexedDBElements = editMode && hasIndexedDBElements;

              return {
                ...page,
                // CRITICAL: Use IndexedDB snapshot if available (it's the latest)
                snapshot:
                  elements.snapshot || page.snapshot || page.canva_snap_url,
                // In edit mode, prefer IndexedDB elements if they exist
                elements: useIndexedDBElements
                  ? {
                      texts: mergedElements.texts || [],
                      rectangles: mergedElements.rectangles || [],
                      circles: mergedElements.circles || [],
                      arrows: mergedElements.arrows || [],
                      scribbles: mergedElements.scribbles || [],
                      lines: mergedElements.lines || [],
                      doubleSidedArrows: mergedElements.doubleSidedArrows || [],
                      images: mergedElements.images || [],
                    }
                  : page.elements || {
                      texts: mergedElements.texts || [],
                      rectangles: mergedElements.rectangles || [],
                      circles: mergedElements.circles || [],
                      arrows: mergedElements.arrows || [],
                      scribbles: mergedElements.scribbles || [],
                      lines: mergedElements.lines || [],
                      doubleSidedArrows: mergedElements.doubleSidedArrows || [],
                      images: mergedElements.images || [],
                    },
                canvasHeight:
                  (typeof elements.canvasHeight === "number"
                    ? elements.canvasHeight
                    : parseInt(elements.canvasHeight)) ??
                  page.canvasHeight ??
                  100,
              };
            } catch (error) {
              return page;
            }
          }),
        );

        // Check if we actually changed anything
        const hasChanges = hydratedPages.some((hydratedPage, idx) => {
          const originalPage = canvasPages[idx];
          if (!originalPage) return true;

          const origElements = originalPage.elements || {};
          const hydratedElements = hydratedPage.elements || {};

          return (
            (origElements.texts?.length || 0) !==
              (hydratedElements.texts?.length || 0) ||
            (origElements.rectangles?.length || 0) !==
              (hydratedElements.rectangles?.length || 0) ||
            (origElements.images?.length || 0) !==
              (hydratedElements.images?.length || 0) ||
            originalPage.snapshot !== hydratedPage.snapshot
          );
        });

        if (hasChanges || shouldForceHydrate) {
          setCanvasPages(hydratedPages);

          // If snapshot was loaded from IndexedDB, also update localStorage
          hydratedPages.forEach((page) => {
            if (
              page.snapshot &&
              page.snapshot !== getCanvasSnapshotFromStorage()
            ) {
              safeSetCanvasSnapshot(page.snapshot);
            }
          });

          hasHydratedRef.current = true;
        } else {
          hasHydratedRef.current = true;
        }
      } catch (error) {
        console.error("Error in early hydration:", error);
        hasHydratedRef.current = true; // Mark as hydrated even on error to prevent loops
      }
    };

    // Run early hydration, especially in edit mode
    if (!hasHydratedRef.current || editMode) {
      hydrateElementsEarly();
    }
  }, [canvasPages.length, editMode]); // Run when canvasPages length changes or editMode changes

  // generateSnapshotFromResponseWithState is now imported from utils/templateUIUtils

  // Reference to hidden file input for image upload
  const fileInputRef = useRef(null);
  // States for webcam functionality
  const [showWebcamPopup, setShowWebcamPopup] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const partNumbers = location.state?.partNumbers;
  const [shortDescription, setShortDescription] = useState(() => {
    const saved = localStorage.getItem("short_description");
    return saved && saved !== "undefined" ? saved : "";
  });

  const [canvasElements, setCanvasElements] = useState({
    images: [],
    canvasHeight: 100, // Default canvas height
  });

  // Debug canvasElements state changes
  useEffect(() => {}, [canvasElements]);
  const [filterType, setFilterType] = useState("all");

  const {
    sectionId,
    stepId,
    revisionId,
    partId,
    partNumber,
    section,
    elementTitle,
    stepTitle,
    revision,
    substepId,
    siblingOrder,
  } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;
  const [comment, setComment] = useState(""); // For filtering parts
  const breadcrumb_text = `${partNumber} > Rev ${revision} > ${section} > ${stepTitle} > ${elementTitle}`;
  const pageTitle = elementTitle;
  const [auditData, setAuditData] = useState({
    user_ref: localStorage.getItem("user_id") || "",
    ip_address: "",
    device_info: navigator.userAgent,
    hash: generateSHA256(Math.random().toString()),
    signature: "",
  }); // Fetch IP address and generate hash
  const handleSaveClick = () => {
    setShowModal(true);
  };
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const getSortIcon = (key) => {
    if (key === "action") return;
    if (sortConfig.key !== key) {
      return (
        <span
          style={{
            display: "flex",
            flexDirection: "column",
            fontSize: "8px",
            position: "absolute",
            right: "5px",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <span>▲</span>
          <span>▼</span>
        </span>
      );
    }
    return sortConfig.direction === "ascending" ? (
      <span
        style={{
          display: "flex",
          flexDirection: "column",
          fontSize: "8px",
          position: "absolute",
          right: "5px",
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        <span style={{ color: "#0D6EFD" }}>▲</span>
        <span>▼</span>
      </span>
    ) : (
      <span
        style={{
          display: "flex",
          flexDirection: "column",
          fontSize: "8px",
          position: "absolute",
          right: "5px",
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        <span>▲</span>
        <span style={{ color: "#0D6EFD" }}>▼</span>
      </span>
    );
  };
  const { categories } = useSelector((state) => state.category);
  // getValidLocalStorageValue is now imported from utils/templateLoadingUtils

  // Add refs to track if data has been fetched to prevent duplicate API calls
  const categoriesFetchedRef = useRef(false);
  const templateFetchedRef = useRef(false);
  const fromMyCanvasProcessedRef = useRef(false); // Track if we've already processed return from MyCanvas

  useEffect(() => {
    if (!categoriesFetchedRef.current) {
      categoriesFetchedRef.current = true;
      dispatch(getAllCategories({ limit: 100, offset: 0 })).then((res) => {});
    }
  }, [dispatch]);

  useEffect(() => {
    if (template) {
      setCategoryId(template.canvas_page_category);
      setShortDescription(template.canvas_page_template_description || "");
      setCanvasName(template.canvas_page_template_name || "");
      setCategoryName(template.category_details?.category_name || "");
    }

    const localShortDesc = getValidLocalStorageValue("short_description");
    if (localShortDesc !== null) setShortDescription(localShortDesc);

    const localCanvasName = getValidLocalStorageValue("canvas_name");
    if (localCanvasName !== null) setCanvasName(localCanvasName);

    const localCategoryName = getValidLocalStorageValue("canvas_category_name");
    if (localCategoryName !== null) setCategoryName(localCategoryName);

    const localCategoryId = getValidLocalStorageValue("canvas_category_id");
    if (localCategoryId !== null) setCategoryId(localCategoryId);
  }, [location.key]); // 👈 triggers on every route visit, even to the same page

  // Load template from API in edit mode, or merge IndexedDB after returning from MyCanvas (add + edit)
  useEffect(() => {
    if (canvasPages.length === 0) {
      setInitialLoading(false);
      return;
    }

    const loadTemplateData = async () => {
        // Prevent duplicate fetch - check if already fetched for this templateId
        if (templateFetchedRef.current === templateId) {
          setInitialLoading(false);
          return;
        }

        try {
          templateFetchedRef.current = templateId; // Mark as fetching/fetched
          setInitialLoading(true);
          setSaveProgress({ step: "Loading template data...", percentage: 10 });

          // Make API call to get complete template data (like CanvasForm.js does)
          const response = await axios.get(
            `${BASE_URL}canvas-page-template/get-by-id/${templateId}`,
          );
          const data = response.data.results;
          setSaveProgress({
            step: "Processing template elements...",
            percentage: 30,
          });

          // Set the fetched template data to state
          setTemplate(data);

          // Set form data from API response
          setCanvasName(data.canvas_page_template_name || "");
          setShortDescription(data.canvas_page_template_description || "");
          setCategoryId(data.canvas_page_category || "");

          // Use the API data instead of the template prop
          const templateData = data;
          // convertApiShapesToMyCanvasFormat is now imported from utils/templateConversionUtils

          // Build single-page elements and snapshot identical to EditCanvasPage flow
          if (
            templateData.pages &&
            Array.isArray(templateData.pages) &&
            templateData.pages.length > 0
          ) {
            const firstPage = templateData.pages[0];
            const pageId =
              firstPage.canvas_page_ref ||
              canvasPages[0]?.id ||
              `template_${Date.now()}`;

            const elements = convertApiShapesToMyCanvasFormat(
              firstPage.shapes || [],
              firstPage, // Pass page data to access layout_config if needed
            );
            attachTextBoxMetadataToTexts(
              elements.texts,
              firstPage.text_boxes || [],
            );
            // Add images (guard against duplicates)
            if (firstPage.images && Array.isArray(firstPage.images)) {
              firstPage.images.forEach((imageData) => {
                if (imageData.image_id.includes("blob")) {
                  return;
                }
                const imgProps = imageData.image_properties || {};
                const imageId =
                  imageData.image_id ||
                  imageData.id ||
                  `api_image_${Date.now()}_${Math.random()}`;

                const alreadyExists = (elements.images || []).some((img) => {
                  const existingKey = img?.id || img?.shape_id || img?.image_id;

                  return (
                    existingKey === imageId ||
                    existingKey === imageData.image_id
                  );
                });

                if (!alreadyExists) {
                  elements.images.push({
                    ...imgProps,
                    x: imgProps.x ?? 0,
                    y: imgProps.y ?? 0,
                    width: imgProps.width ?? 100,
                    height: imgProps.height ?? 100,
                    src: imageData.image_path || "",
                    name: imageData.name || "Picture 1",
                    rotation: imgProps.rotation ?? 0,
                    scaleX: imgProps.scaleX ?? 1,
                    scaleY: imgProps.scaleY ?? 1,
                    compress: imgProps.compress !== false ? true : false, // Default to true if not specified
                    id: imageId,
                    shape_id: imageData.image_id || imageId,
                    image_id: imageId,
                  });
                }
              });
            }

            // Check if canva_snap_url exists in the API response - prefer it over generation
            // Check both template level and page level for canva_snap_url
            let pageSnapshot;
            const templateLevelSnapUrl = templateData.canva_snap_url;
            const pageLevelSnapUrl = firstPage.canva_snap_url;

            if (templateLevelSnapUrl || pageLevelSnapUrl) {
              // Use the existing snapshot URL from API (prefer page level over template level)
              pageSnapshot = pageLevelSnapUrl || templateLevelSnapUrl;

              setSaveProgress({ step: "Loading snapshot...", percentage: 70 });
            } else {
              // Generate snapshot using HTML-based approach for better text rendering

              setSaveProgress({
                step: "Generating snapshot...",
                percentage: 60,
              });
              setSnapshotGenerating(true);
              pageSnapshot = await generateSnapshotFromPageDataHTML(firstPage);
              setSnapshotGenerating(false);
              setSaveProgress({
                step: "Saving to database...",
                percentage: 80,
              });
            }

            // uniqueById is now imported from utils/templateLoadingUtils

            elements.rectangles = uniqueById(elements.rectangles);
            elements.circles = uniqueById(elements.circles);
            elements.arrows = uniqueById(elements.arrows);
            elements.lines = uniqueById(elements.lines);
            elements.doubleSidedArrows = uniqueById(elements.doubleSidedArrows);
            elements.texts = uniqueById(elements.texts);
            elements.images = uniqueById(elements.images);

            // Persist to IndexedDB
            await saveCanvasElementsToIndexedDBWrapper(
              {
                ...elements,
                id: pageId,
                snapshot: pageSnapshot,
                canva_snap_url:
                  pageLevelSnapUrl || templateLevelSnapUrl || null, // Preserve canva_snap_url from template or page
                canvasHeight:
                  firstPage.layout_config?.layout?.page_height_vh || 100,
              },
              pageId,
            );

            // Update UI state
            setCanvasElements({
              ...elements,
              canvasHeight:
                firstPage.layout_config?.layout?.page_height_vh || 100,
            });
            const pageHeightVh =
              firstPage.layout_config?.layout?.page_height_vh || 100;
            updateCanvasPage(0, {
              id: pageId,
              snapshot: pageSnapshot,
              canva_snap_url: pageLevelSnapUrl || templateLevelSnapUrl || null, // Preserve canva_snap_url in page state
              canvasHeight: pageHeightVh, // Store as direct property for easier access
              elements: {
                ...elements,
                canvasHeight: pageHeightVh,
              },
              text_boxes: firstPage.text_boxes || [],
              text_box_content_data: flattenTextBoxContent(
                firstPage.text_boxes || [],
              ),
              hasChanges: false,
            });

            // Persist snapshot to localStorage for consistency
            if (pageSnapshot) {
              safeSetCanvasSnapshot(pageSnapshot);
            }
          } else {
            // Legacy fallback
            const elements = convertApiShapesToMyCanvasFormat(
              (templateData.shape_data || []).map((s) => ({
                type: s.type,
                shape_properties: s.shape_properties || s.shape_info || {},
              })),
              templateData, // Pass template data to access layout_config if needed
            );

            // Check if canva_snap_url exists - prefer it over generation
            // For legacy templates, canva_snap_url is at the template level
            let pageSnapshot;
            const legacyTemplateSnapUrl = templateData.canva_snap_url;

            if (legacyTemplateSnapUrl) {
              // Use the existing snapshot URL from API

              pageSnapshot = legacyTemplateSnapUrl;
              setSaveProgress({ step: "Loading snapshot...", percentage: 70 });
            } else {
              // Generate snapshot from page data

              setSaveProgress({
                step: "Generating snapshot...",
                percentage: 60,
              });
              setSnapshotGenerating(true);
              pageSnapshot = await generateSnapshotFromPageDataHTML({
                shapes: templateData.shape_data || [],
                images: templateData.image_data || [],
              });
              setSnapshotGenerating(false);
              setSaveProgress({
                step: "Saving to database...",
                percentage: 80,
              });
            }

            const pageId = canvasPages[0]?.id || `template_${Date.now()}`;
            await saveCanvasElementsToIndexedDBWrapper(
              {
                ...elements,
                id: pageId,
                snapshot: pageSnapshot,
                canva_snap_url: legacyTemplateSnapUrl || null, // Preserve canva_snap_url if it exists
              },
              pageId,
            );
            setCanvasElements({
              ...elements,
              canvasHeight: 100, // Default for legacy templates
            });
            updateCanvasPage(0, {
              id: pageId,
              snapshot: pageSnapshot,
              canva_snap_url: legacyTemplateSnapUrl || null, // Preserve canva_snap_url in page state
              elements: {
                ...elements,
                canvasHeight: 100, // Default for legacy templates
              },
              text_boxes: [],
              text_box_content_data: [],
              hasChanges: false,
            });
            if (pageSnapshot)
              safeSetCanvasSnapshot(pageSnapshot);
          }
          setSaveProgress({
            step: "Template loaded successfully",
            percentage: 100,
          });
          setInitialLoading(false);
        } catch (error) {
          console.error("Error loading template data:", error);
          templateFetchedRef.current = false; // Reset on error to allow retry
          setInitialLoading(false);
          setSnapshotGenerating(false);
          setSaveProgress({ step: "Error loading template", percentage: 0 });
        }
      };

    // Returning from MyCanvas: merge IndexedDB into page state (add mode + edit mode)
    const fromMyCanvas = localStorage.getItem("fromMyCanvas") === "true";
    const storedPageId = localStorage.getItem("pageId");


    if (fromMyCanvas && storedPageId && !fromMyCanvasProcessedRef.current) {
      loadUpdatedDataFromIndexedDB({
        pageId: storedPageId,
        location,
        template,
        setInitialLoading,
        setSaveProgress,
        setTemplate,
        updateCanvasPage,
        setCanvasElements,
        setCanvasPages,
        fromMyCanvasProcessedRef,
        templateFetchedRef,
      });
      return;
    }

    // Fresh visit: clear stale navigation flags
    if (storedPageId || fromMyCanvas) {
    }
    localStorage.removeItem("fromMyCanvas");
    localStorage.removeItem("pageId");

    if (editMode && templateId) {
      loadTemplateData();
    } else {
      setInitialLoading(false);
    }
  }, [editMode, templateId, canvasPages.length, location.key]);

  // Handle location.state changes when returning from MyCanvas
  useEffect(() => {
    if (location.state?.snapshot && location.state?.fromMyCanvas) {
      // Update canvasPages with the new snapshot
      setCanvasPages((prevPages) => {
        const updatedPages = [...prevPages];
        if (updatedPages[0]) {
          updatedPages[0] = {
            ...updatedPages[0],
            snapshot: location.state.snapshot,
            hasChanges: true,
          };
        }
        return updatedPages;
      });

      // Also save to localStorage for persistence
      safeSetCanvasSnapshot(location.state.snapshot);
    }
  }, [location.state?.snapshot, location.state?.fromMyCanvas]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  // handleSort, showSnackbar, and handleCloseSnackbar are now imported from utils/templateUIUtils
  const handleSort = (key) => handleSortUtil(key, sortConfig, setSortConfig);
  const showSnackbar = (message, type) =>
    showSnackbarUtil(message, type, setSnackbar);
  const handleCloseSnackbar = () =>
    handleCloseSnackbarUtil(snackbar, setSnackbar);

  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) =>
        setAuditData((prev) => ({ ...prev, ip_address: data.ip })),
      )
      .catch((err) => {});
  }, []);

  // Listen for localStorage changes to handle return from MyCanvas
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "fromMyCanvas" && e.newValue === "true") {
        // Trigger a re-render to load updated data
        window.location.reload();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Handle snapshot updates from location.state (returning from MyCanvas)
  // This is more efficient than polling - event-driven updates
  useEffect(() => {
    if (location.state?.snapshot && location.state?.pageId) {
      const returningPageId = location.state.pageId;
      const newSnapshot = location.state.snapshot;

      setCanvasPages((prevPages) => {
        const updatedPages = prevPages.map((page) => {
          if (page.id === returningPageId || !page.snapshot) {
            return {
              ...page,
              snapshot: newSnapshot,
              hasChanges: true,
            };
          }
          return page;
        });

        return updatedPages;
      });

      // Also update localStorage
      if (newSnapshot) {
        safeSetCanvasSnapshot(newSnapshot);
      }

      // Clear location state to prevent re-processing
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.snapshot, location.state?.pageId]);

  // Monitor localStorage for snapshot changes (fallback for cross-tab updates)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "canvasSnapshot" && e.newValue) {
        const newSnapshot = e.newValue;
        const currentSnapshot = canvasPages[0]?.snapshot;

        if (newSnapshot !== currentSnapshot) {
          setCanvasPages((prevPages) => {
            const updatedPages = [...prevPages];
            if (updatedPages[0]) {
              updatedPages[0] = {
                ...updatedPages[0],
                snapshot: newSnapshot,
              };
            }
            return updatedPages;
          });
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [canvasPages[0]?.snapshot]);

  // generateSnapshotFromElements is now imported from utils/snapshotUtils
  // handleCloseModal is now imported from utils/templateUIUtils
  const handleCloseModal = () => handleCloseModalUtil(setShowModal);

  const handleConfirmSave = async () => {
    setIsLoading(true);
    setShowModal(false);
    setSaveProgress({ step: "Saving template...", percentage: 10 });

    try {
      // Before saving (e.g., at the start of handleConfirmSave or before calling it)
      if (!canvasPages || canvasPages.length === 0) {
        const defaultPage = {
          images: [],
          shapes: [],
          saved_styles: null,
          layout_config: {},
          canvas_page_id: null,
          text_box_properties: {},
          text_boxes: [],
          text_box_content_data: [],
        };
        setCanvasPages([defaultPage]);
      }

      // Get the pageId from the first canvas page
      // const pageId = canvasPages[0]?.id || canvasPages[0]?.pageId;
      // if (!pageId) {
      //   throw new Error("No pageId found");
      // }

      // Load updated elements from IndexedDB using a robust pageId resolution
      // This will include any changes made in MyCanvas.js
      const resolvedPageId =
        pageId ||
        localStorage.getItem("pageId") ||
        canvasPages?.[0]?.id ||
        canvasPages?.[0]?.pageId ||
        template?.pages?.[0]?.canvas_page_ref ||
        "currentCanvas";

      const sanitizedResolvedPageId = sanitizeId(resolvedPageId, resolvedPageId);
      let elementsResult = await loadFromIndexedDB(
        "canvasElements",
        resolvedPageId,
      );
      let offCanvasElementsResult = await loadFromIndexedDB(
        "offCanvasElements",
        resolvedPageId,
      );
      if (!elementsResult && sanitizedResolvedPageId !== resolvedPageId) {
        elementsResult = await loadFromIndexedDB(
          "canvasElements",
          sanitizedResolvedPageId,
        );
      }
      if (!offCanvasElementsResult && sanitizedResolvedPageId !== resolvedPageId) {
        offCanvasElementsResult = await loadFromIndexedDB(
          "offCanvasElements",
          sanitizedResolvedPageId,
        );
      }

      // Use the updated elements from IndexedDB (which include MyCanvas changes)
      // or fall back to current canvasElements state if IndexedDB is empty
      const elements = elementsResult?.data || elementsResult || {};
      const offCanvasElements =
        offCanvasElementsResult?.data || offCanvasElementsResult || {};
      const mergedElements = mergeMainAndOffCanvasElements(
        elements,
        offCanvasElements,
      );
      const elementsForSave =
        (mergedElements.texts?.length ||
          mergedElements.rectangles?.length ||
          mergedElements.images?.length ||
          mergedElements.circles?.length ||
          mergedElements.arrows?.length
          ? mergedElements
          : canvasElements) || {
          texts: [],
          rectangles: [],
          circles: [],
          arrows: [],
          scribbles: [],
          lines: [],
          doubleSidedArrows: [],
          images: [],
          canvasHeight: 100, // Default canvas height
        };

      // dedupeById and deduplicateByName are now imported from utils/templateSaveUtils

      const deduplicatedElements = {
        circles: dedupeById(elementsForSave.circles),
        rectangles: dedupeById(elementsForSave.rectangles),
        arrows: dedupeById(elementsForSave.arrows),
        lines: dedupeById(elementsForSave.lines),
        doubleSidedArrows: dedupeById(elementsForSave.doubleSidedArrows),
        images: dedupeById(elementsForSave.images),
        texts: dedupeById(elementsForSave.texts),
        canvasHeight: elementsForSave.canvasHeight, // Preserve canvasHeight for layout config
      };

      const shapeData = [];
      const imageData = [];
      const textBoxContentData = [];
      const textBoxContentDedup = new Set();
      const addTextBoxEntries = (entries = []) => {
        if (!Array.isArray(entries) || entries.length === 0) {
          return;
        }

        entries.forEach((entry) => {
          if (!entry) {
            return;
          }

          const dedupKey =
            entry.text_box_content_id ||
            `${entry.text_box_id || "new"}-${entry.part_ref || ""}-${
              entry.inline_note || ""
            }-${entry.qty ?? 0}-${entry.units || ""}`;

          if (textBoxContentDedup.has(dedupKey)) {
            return;
          }

          textBoxContentDedup.add(dedupKey);
          textBoxContentData.push(entry);
        });
      };

      // pushShapes is now imported from utils/templateSaveUtils

      // Process each element type
      pushShapes(deduplicatedElements.circles, "Circle", shapeData);
      pushShapes(deduplicatedElements.arrows, "Arrow", shapeData);
      pushShapes(deduplicatedElements.lines, "Line", shapeData);
      pushShapes(
        deduplicatedElements.doubleSidedArrows,
        "DoubleSidedArrow",
        shapeData,
      );
      pushShapes(deduplicatedElements.rectangles, "Rect", shapeData);
      pushShapes(deduplicatedElements.texts, "Text", shapeData);

      // Process images with S3 upload
      setSaveProgress({ step: "Processing images...", percentage: 30 });
      for (const img of deduplicatedElements.images || []) {
        const processed = await processImageForSave(img);
        if (processed) {
          imageData.push(processed.imageData);
          shapeData.push(processed.shapeData);
        }
      }

      // Add text content data and collect text_box_properties
      const textBoxPropertiesArray = [];
      (deduplicatedElements.texts || []).forEach((text) => {
        const { text_box_content_data, text_box_properties } =
          convertTextToPayload(text);
        if (text_box_content_data.length > 0) {
          // Filter out units key if it's empty or null
          const filteredTextBoxData = text_box_content_data.map((item) => {
            const filteredItem = { ...item };
            if (
              !filteredItem.units ||
              filteredItem.units === null ||
              filteredItem.units === ""
            ) {
              delete filteredItem.units;
            }
            return filteredItem;
          });
          addTextBoxEntries(filteredTextBoxData);
        }
        // Collect text_box_properties
        if (text_box_properties) {
          textBoxPropertiesArray.push(text_box_properties);
        }
      });

      const currentPageState = canvasPages?.[0] || {};
      if (
        currentPageState.text_box_content_data &&
        Array.isArray(currentPageState.text_box_content_data)
      ) {
        addTextBoxEntries(currentPageState.text_box_content_data);
      } else if (Array.isArray(currentPageState.text_boxes)) {
        addTextBoxEntries(flattenTextBoxContent(currentPageState.text_boxes));
      }
      // Use the first text_box_properties if available (API typically expects one per page)
      const textBoxProperties =
        textBoxPropertiesArray.length > 0 ? textBoxPropertiesArray[0] : {};

      // Get markers from IndexedDB
      const markers = await getMarkersFromIndexedDB();

      // Convert markers array to object format
      const markersObject = markers.reduce((acc, marker) => {
        acc[marker.id] = marker;
        return acc;
      }, {});

      // Generate layout config
      const layoutConfig = generateLayoutConfig(deduplicatedElements);

      // Upload canvas snapshot to S3
      setSaveProgress({ step: "Uploading canvas snapshot...", percentage: 50 });
      let canvasSnapUrl = null;

      // Get the current snapshot from canvasPages or localStorage
      const currentSnapshot =
        canvasPages[0]?.snapshot || getCanvasSnapshotFromStorage();

      if (currentSnapshot) {
        // Check if snapshot is base64 data (needs upload) or already a URL
        if (currentSnapshot.startsWith("data:")) {
          // Snapshot is base64 data URL - upload to S3
          try {
            // Generate unique filename for canvas snapshot
            const timestamp = new Date().getTime();
            const snapshotFileName = `canvas-template-snapshot-${resolvedPageId}-${timestamp}.png`;
            const snapshotFileType = "image/png";

            // Upload snapshot to S3
            canvasSnapUrl = await uploadToS3(
              currentSnapshot,
              snapshotFileName,
              snapshotFileType,
            );
          } catch (error) {
            // Fallback to base64 if S3 upload fails
            canvasSnapUrl = currentSnapshot;
          }
        } else if (currentSnapshot.startsWith("http")) {
          // Snapshot is already a URL, use it directly
          canvasSnapUrl = currentSnapshot;
        } else if (template?.canva_snap_url) {
          // Use canva_snap_url from template if available
          canvasSnapUrl = template.canva_snap_url;
        } else {
          // Unknown format - try to upload anyway as fallback
          try {
            const timestamp = new Date().getTime();
            const snapshotFileName = `canvas-template-snapshot-${resolvedPageId}-${timestamp}.png`;
            const snapshotFileType = "image/png";
            canvasSnapUrl = await uploadToS3(
              currentSnapshot,
              snapshotFileName,
              snapshotFileType,
            );
          } catch (error) {
            // Fallback to original snapshot
            canvasSnapUrl = currentSnapshot;
          }
        }
      } else {
        // Check if template has canva_snap_url as fallback
        if (template?.canva_snap_url) {
          canvasSnapUrl = template.canva_snap_url;
        } else {
        }
      }

      // getRemovedIdsForTemplatePage is now imported from utils/templateSaveUtils

      const removedIds = await getRemovedIdsForTemplatePage(resolvedPageId);

      // Prepare the canvas data
      const canvasdt = {
        canvas_page_template_name: canvasName,
        canvas_page_template_description: shortDescription,
        canvas_page_category: categoryId === "null" ? "" : categoryId,
        layout_config: layoutConfig,
        location_markers: markersObject,
        text_box_properties: textBoxProperties,
        text_box_content_data: textBoxContentData,
        shape_data: shapeData,
        image_data: imageData,
        canva_snap_url: canvasSnapUrl, // Add canvas snapshot URL
        // Removed entities (parity with EditCanvasPage)
        remove_shape_ids: removedIds.shapeIds,
        remove_text_box_content_ids: removedIds.textContentIds,
        remove_text_box_ids: removedIds.textIds,
        remove_image_ids: removedIds.imageIds,
        pageId: template.pages?.[0]?.canvas_page_ref || null,
        textboxId: template.pages?.[0]?.text_boxes?.[0]?.text_box_id || null,
      };

      // Fallback to sessionStorage if template is empty
      if (
        !canvasdt.pageId ||
        !canvasdt.textboxId ||
        !template.canvas_page_template_id
      ) {
        const storedTemplateData = sessionStorage.getItem(
          "canvas_template_data",
        );
        if (storedTemplateData) {
          try {
            const parsedData = JSON.parse(storedTemplateData);
            if (!canvasdt.pageId && parsedData.pageRef) {
              canvasdt.pageId = parsedData.pageRef;
            }
            if (!canvasdt.textboxId && parsedData.textboxId) {
              canvasdt.textboxId = parsedData.textboxId;
            }
          } catch (e) {
            console.error("Error parsing stored template data:", e);
          }
        }
      }

      if (!canvasdt.canvas_page_category) {
        delete canvasdt.canvas_page_category;
      }
      // return;
      // ;
      // return;
      // Dispatch the request with the payload

      // Get template ID with fallback
      let templateIdForUpdate = template.canvas_page_template_id;
      if (!templateIdForUpdate) {
        const storedTemplateData = sessionStorage.getItem(
          "canvas_template_data",
        );
        if (storedTemplateData) {
          try {
            const parsedData = JSON.parse(storedTemplateData);
            templateIdForUpdate = parsedData.canvas_page_template_id;
          } catch (e) {
            console.error("Error getting template ID:", e);
          }
        }
      }

      setSaveProgress({
        step: editMode ? "Updating template..." : "Saving template...",
        percentage: 70,
      });

      const result = (await editMode)
        ? await dispatch(
            updateCanvasPageTemplate({
              canvasdt,
              comment,
              id: templateIdForUpdate,
              version_content: {
                action_to_show: `Save ${elementTitle} for ${partNumber}, Rev ${revision}`,
                partNumber,
                revision,
                section,
                stepTitle,
                elementTitle,
                has_audit_trail_preview: true,
                breadcrumb_text,
                pageTitle,
                canvas_page_template_name: canvasdt.canvas_page_template_name,
                canvas_page_template_description:
                  canvasdt.canvas_page_template_description,
                layout_config: canvasdt.layout_config,
                location_markers: canvasdt.location_markers,
                text_box_properties: canvasdt.text_box_properties,
                text_box_content_data: canvasdt.text_box_content_data,
                shape_data: canvasdt.shape_data,
                image_data: canvasdt.image_data,
                canvas_snap_url: canvasdt.canvas_snap_url,
                revision_id_ref: canvasdt.revision_id_ref,
                section_id_ref: canvasdt.section_id_ref,
                part_required_id_ref: canvasdt.part_required_id_ref,
                substep_id_ref: canvasdt.substep_id_ref,
                step_id_ref: canvasdt.step_id_ref,
              },
            }),
          ).unwrap()
        : await dispatch(
            createCanvasPageTemplate({
              canvasdt,
              comment,
              version_content: {
                action_to_show: `Save ${elementTitle} for ${partNumber}, Rev ${revision}`,
                partNumber,
                revision,
                section,
                stepTitle,
                elementTitle,
                has_audit_trail_preview: true,
                breadcrumb_text,
                pageTitle,
                canvas_page_template_name: canvasdt.canvas_page_template_name,
                canvas_page_template_description:
                  canvasdt.canvas_page_template_description,
                layout_config: canvasdt.layout_config,
                location_markers: canvasdt.location_markers,
                text_box_properties: canvasdt.text_box_properties,
                text_box_content_data: canvasdt.text_box_content_data,
                shape_data: canvasdt.shape_data,
                image_data: canvasdt.image_data,
                canvas_snap_url: canvasdt.canvas_snap_url,
                revision_id_ref: canvasdt.revision_id_ref,
                section_id_ref: canvasdt.section_id_ref,
                part_required_id_ref: canvasdt.part_required_id_ref,
                substep_id_ref: canvasdt.substep_id_ref,
                step_id_ref: canvasdt.step_id_ref,
              },
            }),
          ).unwrap();

      setSaveProgress({
        step: editMode
          ? "Template updated successfully!"
          : "Template saved successfully!",
        percentage: 100,
      });

      showSnackbar(
        editMode
          ? "Canvas template updated successfully!"
          : "Canvas template created successfully!",
        "success",
      );

      // Clear progress message after a short delay
      setTimeout(() => {
        setSaveProgress({ step: "", percentage: 0 });
      }, 2000);

      // Clear all canvas-related data
      updateCanvasPage(0, { snapshot: null });
      clearCanvasSnapshotFromStorage();
      localStorage.removeItem("canvasCompressed");
      sessionStorage.removeItem("canvasStateInfo");
      sessionStorage.removeItem("canvas_template_data"); // Clear stored template data

      // Clear IndexedDB data for this page
      (async () => {
        const db = await openCanvasDB();
        const transaction = db.transaction(["canvasElements"], "readwrite");
        const store = transaction.objectStore("canvasElements");
        const keyToDelete =
          pageId ||
          localStorage.getItem("pageId") ||
          (canvasPages && (canvasPages[0]?.id || canvasPages[0]?.pageId)) ||
          template?.pages?.[0]?.canvas_page_ref ||
          "currentCanvas";

        if (keyToDelete) {
          try {
            store.delete(keyToDelete); // Delete only this page's data
          } catch (err) {}
        } else {
        }
      })();

      setShowModal(false);
      navigate("/canvas-templates", {
        state: { fromAddEdit: true },
      });
    } catch (error) {
      setSaveProgress({ step: "", percentage: 0 });
      showSnackbar(
        error.message ||
          (editMode ? "Error updating canvas" : "Error creating canvas"),
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // generateLayoutConfig and saveCanvasElementsToIndexedDB are now imported from utils/templateUtils
  // Wrapper function to pass convertBlobToBase64 to saveCanvasElementsToIndexedDB
  const saveCanvasElementsToIndexedDBWrapper = async (elements, pageId) => {
    return saveCanvasElementsToIndexedDB(elements, pageId, convertBlobToBase64);
  };

  // handleNavigateToCanvas is now imported from utils/templateNavigationUtils
  const handleNavigateToCanvas = async (canvasPageIndex) => {
    return handleNavigateToCanvasUtil({
      canvasPageIndex,
      canvasPages,
      template,
      templateId,
      partNumbers,
      shortDescription,
      canvasName,
      categoryName,
      categoryId,
      initialLoading,
      snapshotGenerating,
      isLoading,
      callbacks: {
        setIsLoading,
        setSaveProgress,
        navigate,
        saveCanvasElementsToIndexedDB: saveCanvasElementsToIndexedDBWrapper,
      },
      fromMyCanvasProcessedRef,
    });
  };

  const stateInfo = sessionStorage.getItem("canvasStateInfo");

  // Update all handlers to accept idx (page index)

  // Helper to update a specific canvas page
  const updateCanvasPage = (idx, changes) => {
    setCanvasPages((pages) => {
      const updated = pages.map((p, i) =>
        i === idx ? { ...p, ...changes } : p,
      );
      persistCanvasPages(updated);
      return updated;
    });
  };

  // Determine if there are unsaved changes
  let stateHasChanges = false;
  if (stateInfo) {
    try {
      stateHasChanges = JSON.parse(stateInfo).hasChanges;
    } catch (e) {}
  }
  const hasUnsavedChanges = hasCanvasChanges || stateHasChanges;

  // Update all handlers to accept idx (page index)
  const handleZoomIn = (idx) => {
    const currentZoom = canvasPages[idx]?.zoomLevel || 100;
    updateCanvasPage(idx, {
      zoomLevel: Math.min(currentZoom + 10, 200),
    });
  };
  const handleZoomOut = (idx) => {
    const currentZoom = canvasPages[idx]?.zoomLevel || 100;
    updateCanvasPage(idx, {
      zoomLevel: Math.max(currentZoom - 10, 50),
    });
  };

  // addImage and saveToIndexedDB are now imported from utils/templateImageUtils
  // Wrapper function to pass component state callbacks
  const addImage = async (imageSrc, pageId = null) => {
    return addImageToTemplate(imageSrc, pageId, {
      setCanvasElements,
      setCanvasPages,
      updateCanvasPage,
      showFeedback,
      getCanvasPages: () => canvasPages,
      template,
    });
  };

  // Handle webcam button click
  const handleWebcamClick = () => {
    setShowWebcamPopup(true);
  };

  // Function to handle captured image
  useEffect(() => {
    // Only process if we have a capturedImage
    if (capturedImage) {
      // Process the captured image with async wrapper
      const processImage = async () => {
        await addImage(capturedImage);
        // Reset capturedImage state after processing to prevent repeat processing
        setCapturedImage(null);
      };

      processImage();
    }
  }, [capturedImage]);

  // Handle image upload from file input
  const handleImageUpload = (event, pageId = null) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        await addImage(e.target.result, pageId);
      };
      reader.readAsDataURL(file);
    }
  };

  // Function to trigger file input click
  const handleImageButtonClick = () => {
    fileInputRef.current.click();
  };

  // State for feedback messages
  const [feedback, setFeedback] = useState({
    show: false,
    message: "",
    type: "",
  });

  // Function to show feedback
  const showFeedback = (message, type = "success") => {
    setFeedback({ show: true, message, type });
    // Auto-hide after 3 seconds
    setTimeout(() => {
      setFeedback({ show: false, message: "", type: "" });
    }, 3000);
  };

  // addTextToCanvas and handlePasteButtonClick are now imported from utils/templateTextUtils
  // Wrapper function to pass component state callbacks
  const addTextToCanvas = async (text, pageId = null) => {
    return addTextToTemplate(text, pageId, {
      updateCanvasPage,
      setCanvasElements,
      showFeedback,
      getCanvasPages: () => canvasPages,
    });
  };

  const handlePasteButtonClick = () => {
    handlePasteButtonClickUtil({
      showFeedback,
      addTextToCanvas,
    });
  };

  // Add event listener for canvas text updates
  useEffect(() => {
    const handleCanvasTextUpdate = (event) => {
      // Update flags to indicate changes that include text
      sessionStorage.setItem(
        "canvasStateInfo",
        JSON.stringify({
          hasChanges: true,
          lastUpdate: new Date().toISOString(),
          includesText: true,
        }),
      );

      // Try to fetch the latest state from IndexedDB to reflect in UI
      const checkForTextUpdates = async () => {
        try {
          const db = await openCanvasDB();
          const transaction = db.transaction(["canvasElements"], "readonly");
          const store = transaction.objectStore("canvasElements");
          const request = store.get("currentCanvas");

          request.onsuccess = (event) => {
            const canvasData = event.target.result;
            if (canvasData && canvasData.texts && canvasData.texts.length > 0) {
              // Update the state if we have text content
              setCanvasElements((prev) => ({
                ...prev,
                texts: canvasData.texts,
              }));
            }
          };
        } catch (error) {}
      };

      checkForTextUpdates();
    };

    window.addEventListener("canvas-text-updated", handleCanvasTextUpdate);

    return () => {
      window.removeEventListener("canvas-text-updated", handleCanvasTextUpdate);
    };
  }, []);

  return (
    <div>
      {/* AppBar with Navigation Buttons */}
      <AppBar
        title={`${editMode ? "Edit" : "Add"} Canvas Page Template`}
        buttons={[
          {
            label: "Save",
            onClick: canEdit ? handleConfirmSave : undefined,
            disabled: !canEdit,
            title: !canEdit
              ? "You do not have permission to save canvas templates."
              : undefined,
          },
        ]}
        showBlackTitle={false}
        showTablesDropdown={false}
        showProfileButton={false}
      />

      {!initialLoading && (
        <>
          <div className="form-row" style={{ marginLeft: "10px" }}>
            <div className="" style={{ width: "48%" }}>
              <label>Canvas Name</label>
              <input
                type="text"
                name="id"
                value={canvasName}
                onChange={(e) => setCanvasName(e.target.value)}
              />
            </div>
            <div className="" style={{ width: "49%", marginLeft: "20px" }}>
              <label>Category</label>
              <div className="input-with-icon">
                <input
                  style={{
                    borderRadius: "5px 0px 0px 5px",
                    borderRight: "none",
                  }}
                  type="text"
                  value={categoryName}
                  placeholder="Canvas Category"
                  readOnly
                />
                <button
                  type="button"
                  style={{
                    borderRight: "1px solid rgb(215, 215, 215)",
                    borderTop: "1px solid rgb(215, 215, 215)",
                    borderBottom: "1px solid rgb(215, 215, 215)",
                    borderLeft: "none",
                    borderRadius: "0px 5px 5px 0px",
                    backgroundColor: "#d9d9d9",
                    padding: "6.1px 6px",
                  }}
                  className=""
                  onClick={() => setCategoryModalOpen(true)}
                >
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 15 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.3782 5.57534L9.89 2.12329L11.039 0.972603C11.3537 0.657534 11.7401 0.5 12.1983 0.5C12.6566 0.5 13.043 0.657534 13.3577 0.972603L14.5067 2.12329C14.8213 2.43836 14.9855 2.81849 14.9991 3.2637C15.0128 3.7089 14.8624 4.08904 14.5477 4.40411L13.3782 5.57534ZM12.1881 6.78767L3.48817 15.5H0V12.0068L8.69991 3.29452L12.1881 6.78767Z"
                      fill="#0d6efd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          {/* Short Description */}
          <div className="form-group">
            <label
              className="block font-semibold"
              style={{ fontWeight: "bold" }}
            >
              Description
            </label>
            <textarea
              className="w-full border p-2 mt-2"
              placeholder="Enter short description"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              style={{
                minHeight: "100px",
                width: "100%",
              }}
            />
          </div>
          {/* Hidden file input for image upload */}
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleImageUpload}
          />
          {/* Canvas Preview Section */}
          <div style={{ position: "relative", width: "100%" }}>
            {canvasPages.map((page, idx) => (
              <div
                key={idx}
                style={{ position: "relative", marginBottom: "40px" }}
              >
                {/* Main Content Box with 50% width */}
                <div
                  style={{
                    width: "50%",
                    margin: "40px auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    fontFamily: "sans-serif",
                  }}
                >
                  {/* Top Toolbar */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 20px",
                      borderBottom: "1px solid #eee",
                      fontSize: "14px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        backgroundColor: "#f7f7f7",
                        padding: "4px",
                        border: "1px solid #b1b6ba",
                        borderRadius: "3px",
                        visibility: "hidden",
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: "#b1b6ba",
                          color: "white",
                          border: "none",
                          padding: "12px 12px",
                          borderRadius: "50%",
                          fontWeight: 500,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "20px",
                          height: "20px",
                          fontWeight: "bold",
                        }}
                        onClick={() => handleZoomOut(idx)}
                        title="Zoom Out"
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: "12px",
                          color: "#b1b6ba",
                          padding: "4px",
                          borderRadius: "3px",
                          minWidth: "40px",
                          justifyContent: "center",
                          border: "1px solid #b1b6ba",
                        }}
                      >
                        {page.zoomLevel || 100}%
                      </span>
                      <button
                        style={{
                          backgroundColor: "#b1b6ba",
                          color: "white",
                          border: "none",
                          padding: "12px 12px",
                          borderRadius: "50%",
                          fontWeight: 500,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "20px",
                          height: "20px",
                          fontWeight: "bold",
                        }}
                        onClick={() => handleZoomIn(idx)}
                        title="Zoom In"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>
                    <div style={{ color: "#666", textAlign: "center" }}>
                      <i>
                        You are in Preview Mode. <br />
                        To edit click{" "}
                        <span style={{ textDecoration: "underline" }}>
                          "Edit in Full Screen"
                        </span>
                        .
                      </i>
                    </div>
                    <button
                      style={{
                        backgroundColor:
                          isLoading || initialLoading || snapshotGenerating
                            ? "#ccc"
                            : "#007bff",
                        color: "#fff",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "6px",
                        fontWeight: 500,
                        cursor:
                          isLoading || initialLoading || snapshotGenerating
                            ? "not-allowed"
                            : "pointer",
                      }}
                      onClick={() => {
                        !(isLoading || initialLoading || snapshotGenerating) &&
                          handleNavigateToCanvas(idx);
                      }}
                      disabled={
                        isLoading || initialLoading || snapshotGenerating
                      }
                    >
                      {initialLoading
                        ? "Loading Template..."
                        : snapshotGenerating
                          ? "Generating Snapshot..."
                          : isLoading
                            ? "Preparing Canvas..."
                            : "Edit in Full Screen"}
                    </button>
                  </div>
                  {/* Main Display Content */}
                  {page.snapshot ? (
                    <div
                      style={{
                        position: "relative",
                        padding: "20px",
                        cursor: "pointer",
                        textAlign: "center",
                        overflow: "hidden",
                        height:
                          (page.zoomLevel || 100) > 100 ? "420px" : "auto",
                        overflowY:
                          (page.zoomLevel || 100) > 100 ? "auto" : "hidden",
                      }}
                    >
                      <div
                        onClick={() => {
                          if (
                            !(isLoading || initialLoading || snapshotGenerating)
                          ) {
                            handleNavigateToCanvas(idx);
                          }
                        }}
                        style={{
                          marginBottom: "20px",
                          cursor:
                            isLoading || initialLoading || snapshotGenerating
                              ? "not-allowed"
                              : "pointer",
                          opacity:
                            isLoading || initialLoading || snapshotGenerating
                              ? 0.6
                              : 1,
                        }}
                      >
                        {page.snapshot ? (
                          <img
                            src={page.snapshot}
                            alt={`Canvas Snapshot ${idx + 1}`}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "400px",
                              transform: `scale(${
                                (page.zoomLevel || 100) / 100
                              })`,
                              transformOrigin: "center center",
                              transition: "transform 0.2s ease",
                            }}
                            onLoad={() => {}}
                            onError={(e) => {}}
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "400px",
                              backgroundColor: "#f0f0f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "2px dashed #ccc",
                            }}
                          >
                            <p>No snapshot available</p>
                          </div>
                        )}
                      </div>
                      {hasUnsavedChanges && (
                        <div
                          style={{
                            position: "absolute",
                            top: "10px",
                            right: "10px",
                            backgroundColor: "#4CAF50",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            display: "none",
                          }}
                        >
                          Unsaved Changes
                        </div>
                      )}
                      <div
                        style={{
                          marginTop: "10px",
                          backgroundColor: "#007bff",
                          color: "white",
                          padding: "8px 16px",
                          borderRadius: "4px",
                          display: "none",
                        }}
                      >
                        Edit Canvas
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        borderTop: "1px dashed #ccc",
                        padding: "60px 20px",
                        textAlign: "center",
                        cursor: "pointer",
                        backgroundColor: "#f9f9f9",
                      }}
                      onClick={() => {
                        if (
                          !(isLoading || initialLoading || snapshotGenerating)
                        ) {
                          handleNavigateToCanvas(idx);
                        }
                      }}
                    >
                      <svg
                        width="106"
                        height="100"
                        viewBox="0 0 106 100"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M45.1033 99.4921C69.737 99.4921 89.7066 95.7608 89.7066 91.1579C89.7066 86.5551 69.737 82.8237 45.1033 82.8237C20.4696 82.8237 0.5 86.5551 0.5 91.1579C0.5 95.7608 20.4696 99.4921 45.1033 99.4921Z"
                          fill="#F5F5F7"
                          fillOpacity="0.8"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M80.7863 66.6734L65.0462 47.3017C64.291 46.3899 63.187 45.8379 62.0245 45.8379H28.1824C27.0206 45.8379 25.9166 46.3899 25.1613 47.3017L9.42188 66.6734V76.7938H80.787V66.6734H80.7863Z"
                          fill="#AEB8C2"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M22.7566 20.8354H67.4493C68.1473 20.8354 68.8166 21.1127 69.3102 21.6062C69.8037 22.0997 70.0809 22.7691 70.0809 23.467V84.8775C70.0809 85.5755 69.8037 86.2448 69.3102 86.7383C68.8166 87.2319 68.1473 87.5091 67.4493 87.5091H22.7566C22.0586 87.5091 21.3893 87.2319 20.8958 86.7383C20.4023 86.2448 20.125 85.5755 20.125 84.8775V23.467C20.125 22.7691 20.4023 22.0997 20.8958 21.6062C21.3893 21.1127 22.0586 20.8354 22.7566 20.8354V20.8354Z"
                          fill="#F5F5F7"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M28.5791 27.3833H61.6298C61.9787 27.3833 62.3134 27.5219 62.5602 27.7687C62.8069 28.0154 62.9456 28.3501 62.9456 28.6991V45.1182C62.9456 45.4671 62.8069 45.8018 62.5602 46.0486C62.3134 46.2953 61.9787 46.434 61.6298 46.434H28.5791C28.2301 46.434 27.8955 46.2953 27.6487 46.0486C27.402 45.8018 27.2633 45.4671 27.2633 45.1182V28.6991C27.2633 28.3501 27.402 28.0154 27.6487 27.7687C27.8955 27.5219 28.2301 27.3833 28.5791 27.3833ZM28.7515 53.5767H61.4574C61.8521 53.5767 62.2306 53.7335 62.5097 54.0126C62.7888 54.2917 62.9456 54.6702 62.9456 55.0649C62.9456 55.4596 62.7888 55.8381 62.5097 56.1172C62.2306 56.3962 61.8521 56.553 61.4574 56.553H28.7515C28.3568 56.553 27.9783 56.3962 27.6992 56.1172C27.4201 55.8381 27.2633 55.4596 27.2633 55.0649C27.2633 54.6702 27.4201 54.2917 27.6992 54.0126C27.9783 53.7335 28.3568 53.5767 28.7515 53.5767ZM28.7515 61.3155H61.4574C61.8522 61.3155 62.2308 61.4724 62.5099 61.7515C62.7891 62.0307 62.9459 62.4093 62.9459 62.804C62.9459 63.1988 62.7891 63.5774 62.5099 63.8565C62.2308 64.1357 61.8522 64.2925 61.4574 64.2925H28.7515C28.3567 64.2925 27.9781 64.1357 27.699 63.8565C27.4198 63.5774 27.263 63.1988 27.263 62.804C27.263 62.4093 27.4198 62.0307 27.699 61.7515C27.9781 61.4724 28.3567 61.3155 28.7515 61.3155ZM80.6416 89.9353C80.1318 91.9557 78.341 93.4616 76.2107 93.4616H13.9982C11.8679 93.4616 10.0771 91.955 9.56793 89.9353C9.47081 89.5503 9.42175 89.1547 9.42188 88.7576V66.6741H26.7363C28.6488 66.6741 30.1903 68.2846 30.1903 70.2399V70.2662C30.1903 72.2208 31.7495 73.7991 33.662 73.7991H56.5469C58.4594 73.7991 60.0186 72.2063 60.0186 70.2511V70.2432C60.0186 68.2879 61.56 66.6734 63.4725 66.6734H80.787V88.7583C80.787 89.1642 80.7364 89.5583 80.6416 89.9353Z"
                          fill="#DCE0E6"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M82.8201 21.9026L78.3266 23.6461C78.2112 23.6909 78.0854 23.7023 77.9638 23.6789C77.8422 23.6555 77.7297 23.5982 77.6392 23.5137C77.5486 23.4292 77.4838 23.3208 77.4522 23.2011C77.4205 23.0813 77.4233 22.9551 77.4602 22.8368L78.7345 18.7533C77.0312 16.8164 76.0312 14.4546 76.0312 11.9059C76.0312 5.33026 82.6878 0 90.8997 0C99.1095 0 105.767 5.33026 105.767 11.9059C105.767 18.4816 99.1102 23.8118 90.899 23.8118C87.9201 23.8118 85.1464 23.1105 82.8201 21.9026Z"
                          fill="#DCE0E6"
                        />
                        <path
                          d="M96.7493 14.056C97.7845 14.056 98.6237 13.2268 98.6237 12.204C98.6237 11.1812 97.7845 10.3521 96.7493 10.3521C95.7142 10.3521 94.875 11.1812 94.875 12.204C94.875 13.2268 95.7142 14.056 96.7493 14.056Z"
                          fill="white"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M86.9127 13.8243H83.1641L85.0706 10.5835L86.9127 13.8243ZM89.2555 10.5835H92.5351V13.8243H89.2555V10.5835Z"
                          fill="white"
                        />
                      </svg>
                      <p
                        style={{
                          marginBottom: "15px",
                          color: "#666",
                          fontSize: "16px",
                        }}
                      >
                        No Data Available
                      </p>
                      <button
                        style={{
                          backgroundColor: "#007bff",
                          color: "white",
                          padding: "8px 16px",
                          borderRadius: "4px",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                      >
                        Create New Canvas
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Display Loader during initial data loading */}
      {initialLoading && (
        <Loader text={saveProgress.step || "Loading template data..."} />
      )}

      {!initialLoading && (
        <>
          {/* Loader for other operations */}
          {isLoading && (
            <Loader text={saveProgress.step || "Saving canvas template..."} />
          )}
          {snapshotGenerating && <Loader text="Generating snapshot..." />}
          {/* Confirmation Modal */}
          {showModal && (
            <div className="modal-overlay">
              <div className="modal-content_addsection">
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <h4
                    style={{
                      marginLeft: "23px",
                      fontSize: "18px",
                      color: "#333333",
                    }}
                  >
                    Save Canvas Page Template
                  </h4>
                  <button
                    onClick={handleCloseModal}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "10px",
                      background: "none",
                      border: "none",
                      fontSize: "20px",
                      cursor: "pointer",
                      color: "#666",
                    }}
                  >
                    ✕
                  </button>
                </div>

                {/* Modal Body */}
                {/* <div
              style={{ padding: "24px", marginTop: "-28px", color: "#333333" }}
            >
             
              <p
                style={{
                  fontSize: "14px",
                  color: "#333333",
                  marginBottom: "10px",
                }}
              >
                <strong>Editor:</strong>{" "}
                <span style={{ marginLeft: "10px" }}>
                  {localStorage.getItem("user_name")}
                </span>
              </p>
              <label
                className="modal-label"
                style={{ fontSize: "14px", color: "#333333" }}
              >
                Comment<span className="required">*</span>
              </label>
              <textarea
                className="modal-textarea"
                placeholder="Enter your comments here..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                style={{
                  width: "100%",
                  minHeight: "100px",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              ></textarea>
            </div> */}

                {/* Footer */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    padding: "16px 24px",
                    borderTop: "1px solid #e0e0e0",
                    marginTop: "-10px",
                  }}
                >
                  <button
                    onClick={handleCloseModal}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      background: "#6c757d",
                      color: "#e9ecef",
                      cursor: "pointer",
                    }}
                  >
                    Close
                  </button>
                  <button
                    onClick={handleConfirmSave}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #007bff",
                      borderRadius: "4px",
                      background: "#007bff",
                      color: "white",
                      cursor: "pointer",
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Webcam Popup */}
          {showWebcamPopup && (
            <WebcamPopup
              setShowWebcamPopup={setShowWebcamPopup}
              setCapturedImage={setCapturedImage}
            />
          )}
          {snackbar.open && (
            <Snackbar
              message={snackbar.message}
              type={snackbar.type}
              onClose={handleCloseSnackbar}
            />
          )}
          {categoryModalOpen && (
            <CategoryPopup
              handleSelectCategory={(category) => {
                setCategoryId(category.category_id);
                setCategoryName(category.category_name);
                setCategoryModalOpen(false);
              }}
              onClose={setCategoryModalOpen}
              disableFilter={true}
              cat_type={"Canvas > Canvas Template"}
            />
          )}
          {/* Feedback Message */}
          {feedback.show && (
            <div
              style={{
                position: "fixed",
                top: "20px",
                right: "20px",
                backgroundColor:
                  feedback.type === "error" ? "#f44336" : "#4CAF50",
                color: "white",
                padding: "10px 20px",
                borderRadius: "4px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                zIndex: 1000,
                maxWidth: "300px",
                animation: "fadeIn 0.3s ease",
              }}
            >
              {feedback.message}
            </div>
          )}
        </>
      )}

      <div style={{ height: "100px" }}></div>
    </div>
  );
};

export default AddCanvasTemplate;
